import {all, call, put, takeEvery, takeLatest} from "redux-saga/effects";
import {AuthorizedRequestBuilder} from "utils/sagaBuilder";
import {generic} from "utils/action";
import {authorizedRequest, HTTP_METHODS} from "utils/request";
import * as CONSTANTS from "./constants";
import moment from "moment";
import {GET_PLANNED_DAYS_FAILED} from "containers/TimeSlotPage/constants";

// Individual exports for testing

export function* getActiveTreatmentsSaga({ patientId }) {
  try {
    const result = yield call(
      authorizedRequest,
      CONSTANTS.ACTIVE_TREATMENTS_URL(patientId),
    );
    yield put(generic(CONSTANTS.GET_TREATMENT_PLAN_SUCCESS, { result }));
  } catch (e) {
    yield put(
      generic(CONSTANTS.GET_TREATMENT_PLAN_FAILED, { reason: e.response.data }),
    );
  }
}

export const deletePlannedTreatmentSaga = new AuthorizedRequestBuilder(
  CONSTANTS.DELETE_PLANNED_TREATMENT_SUCCESS,
  CONSTANTS.DELETE_PLANNED_TREATMENT_FAILED,
)
  .method(HTTP_METHODS.DELETE)
  .url(CONSTANTS.PLANNED_TREATMENTS_URL)
  .callbackBuilder(({ callback }) => callback)
  .body(({ treatment }) => ({
    id: treatment.id,
  }))
  .build();

export function* updateTreatmentPlanSaga({ treatmentPlan, callback }) {
  try {
    const result = yield call(
      authorizedRequest,
      CONSTANTS.TREATMENT_PLAN_URL(),
      {
        method: HTTP_METHODS.PUT,
        body: treatmentPlan,
      },
    );
    yield put(generic(CONSTANTS.UPDATE_TREATMENT_PLAN_SUCCESS, { result }));
    yield call(callback);
  } catch (e) {
    yield put(
      generic(CONSTANTS.UPDATE_TREATMENT_PLAN_FAILED, {
        reason: e.response.data,
      }),
    );
  }
}

export function* createTreatmentPlanSaga({ treatmentPlan, callback }) {
  try {
    const result = yield call(
      authorizedRequest,
      CONSTANTS.TREATMENT_PLAN_URL(),
      {
        method: HTTP_METHODS.POST,
        body: treatmentPlan,
      },
    );

    yield put(generic(CONSTANTS.CREATE_TREATMENT_PLAN_SUCCESS, { result }));
    yield call(callback);
  } catch (e) {
    yield put(
      generic(CONSTANTS.CREATE_TREATMENT_PLAN_FAILED, {
        reason: e.response.data,
      }),
    );
  }
}

export const getPatientSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_PATIENT_SUCCESS,
  CONSTANTS.GET_PATIENT_FAILED,
)
  .url(({ patientId }) => CONSTANTS.PATIENT_URL(patientId))
  .build();

export const updatePatientSaga = new AuthorizedRequestBuilder(
  CONSTANTS.UPDATE_PATIENT_SUCCESS,
  CONSTANTS.UPDATE_PATIENT_FAILED,
)
  .method(HTTP_METHODS.PUT)
  .body(({ patient }) => patient)
  .url(CONSTANTS.UPDATE_PATIENT_URL)
  .build();
export const patientByIdSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_PATIENT_ID_SUCCESS,
  CONSTANTS.GET_PATIENT_ID_FAILED,
)
  .url(({ patientId }) => CONSTANTS.PATIENT_ID_URL(patientId))
  .build();

export function* getTreatmentPlanByIdSaga({ treatmentPlanId }) {
  // Get all the end products first.
  try {
    const treatmentPlan = yield call(
      authorizedRequest,
      CONSTANTS.TREATMENT_PLAN_ID_URL(treatmentPlanId),
    );

    // Get the patient corresponding with the treatment
    yield put(
      generic(CONSTANTS.GET_PATIENT_ID, { patientId: treatmentPlan.patientId }),
    );

    // Store the treatment
    yield put(
      generic(CONSTANTS.GET_TREATMENT_PLAN_ID_SUCCESS, {
        result: treatmentPlan,
      }),
    );
  } catch (e) {
    yield put(
      generic(CONSTANTS.GET_TREATMENT_PLAN_ID_FAILED, {
        reason: e.response.data,
      }),
    );
  }
}

export function* getTreatmentDriversSaga() {
  try {
    const drivers = yield call(authorizedRequest, "/drivers");

    yield put(
      generic(CONSTANTS.GET_TREATMENT_DRIVERS_SUCCESS, {
        drivers,
      }),
    );
  } catch (e) {
    yield put(
      generic(CONSTANTS.GET_TREATMENT_DRIVERS_ERROR, {
        reason: e.response.data,
      }),
    );
  }
}

export const timeSlotDataSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_TIME_SLOT_DATA_SUCCESS,
  CONSTANTS.GET_TIME_SLOT_DATA_FAILED,
)
  .url(({ timeSlotId }) => CONSTANTS.TIME_SLOT_DATA_URL(timeSlotId))
  .build();

export const getPlannedDaysSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_PLANNED_DAYS_SUCCESS,
  GET_PLANNED_DAYS_FAILED,
)
  .url(action => CONSTANTS.GET_PLANNED_DAYS_URL(action.date))
  .build();

export const checkAvailabilitySaga = new AuthorizedRequestBuilder(
  CONSTANTS.CHECK_AVAILABILITY_SUCCESS,
  CONSTANTS.CHECK_AVAILABILITY_FAILED,
)
  .method(HTTP_METHODS.POST)
  .url(CONSTANTS.CHECK_AVAILABILITY_URL)
  .callbackBuilder((action, result) => () => {
    action.callback(result);
  })
  .body(({ date, timeSlot, toPlan, kptOverride, treatmentDurationCorrection, manufacturerOffice, withScanner }) => {
    const body = {
      data: {
        activity: {
          earliest_delivery_date: moment(date).format("DD-MM-YYYY"),
          allowed_driver_ids: kptOverride ? [kptOverride] : [],
        },
      },
      treatmentToPlan: toPlan,
      treatmentDurationCorrection: treatmentDurationCorrection,
      manufacturerOffice: manufacturerOffice,
      withScanner: withScanner
    };
    if (timeSlot) {
      body.plannedTimeSlot = {
        id: timeSlot.id,
      };
    }
    return body;
  })
  .build();

export function* getTreatmentDurationSaga({tpId, homeShipping}) {
  try {
    const pt = yield call(authorizedRequest, CONSTANTS.TREATMENT_DURATION_URL(tpId,homeShipping));

    yield put(
      generic(CONSTANTS.GET_TREATMENT_DURATION_SUCCESS, {
        duration:pt.treatmentDuration,
      }),
    );
  } catch (e) {
    yield put(
      generic(CONSTANTS.GET_TREATMENT_DURATION_ERROR, {
        reason: e.response.data,
      }),
    );
  }
}

export default function* newTreatmentPageSaga() {
  yield all([
    takeEvery(CONSTANTS.GET_TREATMENT_PLAN, getActiveTreatmentsSaga),
    takeEvery(CONSTANTS.UPDATE_TREATMENT_PLAN, updateTreatmentPlanSaga),
    takeEvery(CONSTANTS.CREATE_TREATMENT_PLAN, createTreatmentPlanSaga),
    takeEvery(CONSTANTS.DELETE_PLANNED_TREATMENT, deletePlannedTreatmentSaga),
    takeEvery(CONSTANTS.GET_PATIENT, getPatientSaga),
    takeEvery(CONSTANTS.UPDATE_PATIENT, updatePatientSaga),
    takeEvery(CONSTANTS.GET_TIME_SLOT_DATA, timeSlotDataSaga),
    takeEvery(CONSTANTS.GET_TREATMENT_PLAN_ID, getTreatmentPlanByIdSaga),
    takeEvery(CONSTANTS.GET_PATIENT_ID, patientByIdSaga),
    takeLatest(CONSTANTS.CHECK_AVAILABILITY, checkAvailabilitySaga),
    takeLatest(CONSTANTS.GET_PLANNED_DAYS, getPlannedDaysSaga),
    takeLatest(CONSTANTS.GET_TREATMENT_DRIVERS, getTreatmentDriversSaga),
    takeLatest(CONSTANTS.GET_TREATMENT_DURATION, getTreatmentDurationSaga)
  ]);
}
